/**
 * Privacy Center — DPDP (India) Phase 1
 *
 * Auto-switches view based on user role:
 *   - Any user → MyPrivacy (consent toggles + request export / erasure)
 *   - company_admin / super_admin → also gets the admin queue tab
 *
 * Backend: /api/dpdp/* (see dpdp_routes.py)
 */
import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import AppShell from "../components/AppShell";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { ShieldCheck, Download, Trash, Warning, CheckCircle, Clock, FileText } from "@phosphor-icons/react";
import { toast } from "sonner";

const ADMIN_ROLES = ["company_admin", "super_admin"];

export default function PrivacyCenter() {
  const { user } = useAuth();
  const canAdmin = ADMIN_ROLES.includes(user?.role);
  return (
    <AppShell title="Privacy Center">
      <div className="max-w-5xl mx-auto" data-testid="privacy-center">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-zinc-900">Privacy Center</h1>
          <p className="text-sm text-zinc-600 mt-1">
            Manage your consent preferences and exercise your rights under India&apos;s Digital
            Personal Data Protection Act, 2023.
          </p>
        </div>
        {canAdmin ? (
          <Tabs defaultValue="me" className="w-full">
            <TabsList>
              <TabsTrigger value="me" data-testid="tab-me">My data</TabsTrigger>
              <TabsTrigger value="queue" data-testid="tab-queue">DSAR queue (admin)</TabsTrigger>
            </TabsList>
            <TabsContent value="me" className="mt-6"><MyPrivacy /></TabsContent>
            <TabsContent value="queue" className="mt-6"><AdminQueue /></TabsContent>
          </Tabs>
        ) : (
          <MyPrivacy />
        )}
      </div>
    </AppShell>
  );
}

/* ──────────────────────────────────────────────────────────────────────── */
/* User self-service                                                         */
/* ──────────────────────────────────────────────────────────────────────── */
function MyPrivacy() {
  const [state, setState] = useState({ loading: true });
  const [purposes, setPurposes] = useState({});
  const [saving, setSaving] = useState(false);
  const [requests, setRequests] = useState([]);
  const [dsarDialog, setDsarDialog] = useState(null); // 'export' | 'erase' | null
  const [reason, setReason] = useState("");

  const load = async () => {
    try {
      const [me, mine] = await Promise.all([
        api.get("/dpdp/consent/me"),
        api.get("/dpdp/dsar/me"),
      ]);
      const p = {};
      (me.data.purposes || []).forEach((x) => {
        p[x.key] = me.data.consent?.purposes?.[x.key] ?? x.required ?? false;
      });
      setPurposes(p);
      setState({ ...me.data, loading: false });
      setRequests(mine.data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
      setState({ loading: false, error: true });
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/dpdp/consent", {
        privacy_policy: true,
        dpa: true,
        cookies: !!purposes.cookies_extra || true,
        purposes,
      });
      toast.success("Consent preferences saved");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const submitDsar = async () => {
    try {
      await api.post("/dpdp/dsar/request", {
        kind: dsarDialog,
        reason: reason.trim() || null,
      });
      toast.success(`${dsarDialog === "export" ? "Export" : "Erasure"} request submitted`);
      setDsarDialog(null); setReason("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadExport = async (rid) => {
    try {
      const r = await api.get(`/dpdp/dsar/export/${rid}/download`);
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `my-data-export-${rid.slice(0, 8)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (state.loading) return <Card className="p-6 text-sm text-zinc-500">Loading…</Card>;
  if (state.error) return <Card className="p-6 text-sm text-red-600">Couldn&apos;t load. Refresh the page.</Card>;

  return (
    <div className="space-y-6">
      {/* Renewal banner */}
      {state.needs_renewal && (
        <Card className="p-4 border-amber-200 bg-amber-50 flex items-start gap-3" data-testid="consent-banner">
          <Warning size={20} weight="duotone" className="text-amber-700 mt-0.5" />
          <div className="flex-1">
            <div className="font-medium text-sm text-amber-900">Privacy notice updated</div>
            <div className="text-xs text-amber-800 mt-0.5">
              We&apos;ve updated our privacy policy (v {state.current_versions?.privacy_policy}).
              Please review and re-confirm your preferences below.
            </div>
          </div>
        </Card>
      )}

      {/* Consent card */}
      <Card className="p-6" data-testid="consent-card">
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck size={20} weight="duotone" className="text-emerald-600" />
          <h2 className="font-semibold text-zinc-900">Consent preferences</h2>
        </div>
        <p className="text-xs text-zinc-500 mb-4">
          You can change any non-essential consent at any time.
        </p>
        <div className="space-y-4">
          {(state.purposes || []).map((p) => (
            <div key={p.key} className="flex items-start justify-between border-b border-zinc-100 pb-3 last:border-b-0">
              <div className="flex-1 pr-4">
                <div className="text-sm font-medium text-zinc-900">{p.label}</div>
                {p.required && <div className="text-[11px] text-zinc-500 mt-0.5">Required — cannot be revoked.</div>}
              </div>
              <Switch
                checked={!!purposes[p.key]}
                disabled={p.required}
                onCheckedChange={(v) => setPurposes((cur) => ({ ...cur, [p.key]: v }))}
                data-testid={`consent-toggle-${p.key}`}
              />
            </div>
          ))}
        </div>
        <div className="mt-5 flex items-center justify-between">
          <div className="text-xs text-zinc-500">
            Policy v {state.current_versions?.privacy_policy} ·
            DPA v {state.current_versions?.dpa}
          </div>
          <Button onClick={save} disabled={saving} data-testid="save-consent-btn">
            {saving ? "Saving…" : "Save preferences"}
          </Button>
        </div>
      </Card>

      {/* Rights / DSAR */}
      <Card className="p-6" data-testid="dsar-actions">
        <h2 className="font-semibold text-zinc-900 mb-1">Your rights</h2>
        <p className="text-xs text-zinc-500 mb-4">
          Under DPDP §11–§13 you may request a copy of your data or its erasure.
          We honour DSAR within 30 days unless we have a lawful retention basis
          (e.g. payroll / tax records — 7 years per Indian law).
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <button
            onClick={() => setDsarDialog("export")}
            className="flex items-start gap-3 border border-zinc-200 rounded-lg p-4 text-left hover:border-zinc-300 hover:bg-zinc-50 transition"
            data-testid="request-export-btn"
          >
            <Download size={22} weight="duotone" className="text-indigo-600" />
            <div>
              <div className="font-medium text-sm text-zinc-900">Request a data export</div>
              <div className="text-xs text-zinc-500 mt-0.5">Download a JSON of every record we hold about you.</div>
            </div>
          </button>
          <button
            onClick={() => setDsarDialog("erase")}
            className="flex items-start gap-3 border border-zinc-200 rounded-lg p-4 text-left hover:border-red-300 hover:bg-red-50 transition"
            data-testid="request-erase-btn"
          >
            <Trash size={22} weight="duotone" className="text-red-600" />
            <div>
              <div className="font-medium text-sm text-zinc-900">Request erasure</div>
              <div className="text-xs text-zinc-500 mt-0.5">Anonymise your personal identifiers. Subject to admin approval.</div>
            </div>
          </button>
        </div>
      </Card>

      {/* History */}
      <Card className="p-6" data-testid="dsar-history">
        <h2 className="font-semibold text-zinc-900 mb-3">My requests</h2>
        {requests.length === 0 ? (
          <div className="text-sm text-zinc-500 italic">No requests yet.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Type</TableHead>
                <TableHead>Submitted</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {requests.map((r) => (
                <TableRow key={r.id} data-testid={`dsar-row-${r.id}`}>
                  <TableCell className="font-medium capitalize">{r.kind}</TableCell>
                  <TableCell className="text-xs text-zinc-500">{(r.submitted_at || "").slice(0, 16).replace("T", " ")}</TableCell>
                  <TableCell>
                    <Badge variant={r.status === "pending" ? "secondary" : r.status === "rejected" ? "destructive" : "default"}>
                      {r.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    {r.kind === "export" && (r.status === "approved" || r.status === "fulfilled") ? (
                      <Button size="sm" variant="outline" onClick={() => downloadExport(r.id)} data-testid="download-export-btn">
                        Download
                      </Button>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {/* DSAR dialog */}
      <Dialog open={!!dsarDialog} onOpenChange={(o) => !o && setDsarDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {dsarDialog === "export" ? "Request a data export" : "Request erasure"}
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-zinc-600">
            {dsarDialog === "erase" ? (
              <>
                <p>Erasure soft-anonymises your name, email, phone, and other identifiers. Statutory
                records (payroll, tax, attendance for the legally-mandated retention) are retained
                but de-linked from your identity.</p>
                <p className="mt-2 text-red-700">This cannot be undone once an admin approves it.</p>
              </>
            ) : (
              <p>We will package every record we hold about you (profile, attendance, payroll,
              expenses, leaves, performance reviews, audit trail, etc.) into a single JSON file
              you can download. Admin must approve first.</p>
            )}
          </div>
          <Textarea
            placeholder="Optional — tell us why you're requesting this"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            className="mt-2"
            data-testid="dsar-reason"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDsarDialog(null)}>Cancel</Button>
            <Button onClick={submitDsar} data-testid="submit-dsar-btn">Submit request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────── */
/* Admin queue                                                               */
/* ──────────────────────────────────────────────────────────────────────── */
function AdminQueue() {
  const [data, setData] = useState({ items: [], open: 0, sla_breached: 0, loading: true });
  const [decideTarget, setDecideTarget] = useState(null); // {id, decision}
  const [note, setNote] = useState("");

  const load = async () => {
    try {
      const r = await api.get("/dpdp/dsar/queue");
      setData({ ...r.data, loading: false });
    } catch (e) { toast.error(formatApiError(e)); setData((d) => ({ ...d, loading: false })); }
  };
  useEffect(() => { load(); }, []);

  const decide = async () => {
    try {
      await api.post(`/dpdp/dsar/queue/${decideTarget.id}/decide`, {
        decision: decideTarget.decision,
        note: note.trim() || null,
      });
      toast.success(`Request ${decideTarget.decision}d`);
      setDecideTarget(null); setNote("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (data.loading) return <Card className="p-6 text-sm text-zinc-500">Loading…</Card>;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <Card className="p-4" data-testid="stat-open">
          <div className="text-xs text-zinc-500">Open requests</div>
          <div className="text-2xl font-semibold text-zinc-900 mt-1">{data.open}</div>
        </Card>
        <Card className="p-4" data-testid="stat-breached">
          <div className="text-xs text-zinc-500">SLA breached (&gt; 30 days)</div>
          <div className={`text-2xl font-semibold mt-1 ${data.sla_breached ? "text-red-600" : "text-zinc-900"}`}>{data.sla_breached}</div>
        </Card>
        <Card className="p-4" data-testid="stat-total">
          <div className="text-xs text-zinc-500">Total this tenant</div>
          <div className="text-2xl font-semibold text-zinc-900 mt-1">{data.items.length}</div>
        </Card>
      </div>
      <Card className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>User</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead>Submitted</TableHead>
              <TableHead>SLA</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.items.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center text-sm text-zinc-500 italic py-6">No DSAR requests yet.</TableCell></TableRow>
            ) : data.items.map((r) => {
              const overdue = r.status === "pending" && r.sla_deadline && new Date(r.sla_deadline) < new Date();
              return (
                <TableRow key={r.id} data-testid={`queue-row-${r.id}`}>
                  <TableCell className="text-xs">{r.email}</TableCell>
                  <TableCell className="capitalize">{r.kind}</TableCell>
                  <TableCell className="text-xs text-zinc-500 max-w-[200px] truncate">{r.reason || "—"}</TableCell>
                  <TableCell className="text-xs text-zinc-500">{(r.submitted_at || "").slice(0, 10)}</TableCell>
                  <TableCell>
                    {overdue ? (
                      <Badge variant="destructive">overdue</Badge>
                    ) : (
                      <span className="text-xs text-zinc-500">{(r.sla_deadline || "").slice(0, 10)}</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant={r.status === "pending" ? "secondary" : r.status === "rejected" ? "destructive" : "default"}>
                      {r.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    {r.status === "pending" && (
                      <div className="flex justify-end gap-2">
                        <Button size="sm" variant="outline" onClick={() => setDecideTarget({ id: r.id, decision: "reject" })} data-testid={`reject-${r.id}`}>Reject</Button>
                        <Button size="sm" onClick={() => setDecideTarget({ id: r.id, decision: "approve" })} data-testid={`approve-${r.id}`}>Approve</Button>
                      </div>
                    )}
                    {r.status === "approved" && r.kind === "erase" && (
                      <Button size="sm" variant="destructive" onClick={() => setDecideTarget({ id: r.id, decision: "fulfill" })} data-testid={`fulfill-${r.id}`}>Erase now</Button>
                    )}
                    {r.status === "approved" && r.kind === "export" && (
                      <Button size="sm" variant="outline" onClick={async () => {
                        try {
                          const x = await api.get(`/dpdp/dsar/export/${r.id}/download`);
                          const blob = new Blob([JSON.stringify(x.data, null, 2)], { type: "application/json" });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement("a");
                          a.href = url; a.download = `dsar-export-${r.id.slice(0, 8)}.json`;
                          a.click(); URL.revokeObjectURL(url);
                        } catch (e) { toast.error(formatApiError(e)); }
                      }} data-testid={`bundle-${r.id}`}>Get bundle</Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!decideTarget} onOpenChange={(o) => !o && setDecideTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {decideTarget?.decision === "approve" ? "Approve request"
                : decideTarget?.decision === "reject" ? "Reject request"
                : "Fulfill erasure"}
            </DialogTitle>
          </DialogHeader>
          {decideTarget?.decision === "fulfill" && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">
              This will anonymise the user&apos;s name, email, phone, PAN/Aadhaar, bank details, and
              addresses. Statutory records remain but become un-linkable to identity.
              <strong className="block mt-1">This cannot be undone.</strong>
            </div>
          )}
          <Textarea
            placeholder="Decision note (optional, shared with the requester)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            data-testid="decision-note"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDecideTarget(null)}>Cancel</Button>
            <Button onClick={decide} data-testid="confirm-decision-btn">Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
